import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

export class MenuShowcase extends UcoastEl {
	static htmlSelector = 'menu-showcase'

	private tabs: HTMLButtonElement[] = []
	private items: HTMLElement[] = []
	private tiles: HTMLButtonElement[] = []
	private stageImages: HTMLElement[] = []
	private stacks: HTMLElement[] = []
	private detailToggles: HTMLButtonElement[] = []
	private detailPanel?: HTMLElement
	private onKeydown?: (e: KeyboardEvent) => void
	private onDocClick?: (e: MouseEvent) => void
	private onFloatingNavOpen?: () => void

	override connectedCallback() {
		super.connectedCallback()

		this.tabs = Array.from(q.ol<HTMLButtonElement>('[data-menu-tab]', this) ?? [])
		this.items = Array.from(q.ol<HTMLElement>('[data-menu-item]', this) ?? [])
		this.tiles = Array.from(q.ol<HTMLButtonElement>('[data-menu-item-trigger]', this) ?? [])
		this.stageImages = Array.from(q.ol<HTMLElement>('[data-menu-stage-image]', this) ?? [])
		this.stacks = Array.from(q.ol<HTMLElement>('[data-menu-stack]', this) ?? [])
		this.detailToggles = Array.from(q.ol<HTMLButtonElement>('[data-menu-detail-toggle]', this) ?? [])
		this.detailPanel = q.os<HTMLElement>('[data-menu-detail]', this)

		this.bindTabs()
		this.bindTiles()
		this.bindDetail()
		this.bindDismiss()

		const firstActive = this.tiles.find((t) => t.getAttribute('aria-pressed') === 'true')
		if (firstActive) {
			firstActive.classList.add('is-active')
			const handle = this.handleFromTile(firstActive)
			if (handle) this.markStageImage(handle, true)
		}
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown)
		if (this.onDocClick) document.removeEventListener('click', this.onDocClick)
		if (this.onFloatingNavOpen) document.removeEventListener('floating-nav:open', this.onFloatingNavOpen)
	}

	private handleFromTile(tile: HTMLElement): string | undefined {
		const li = tile.closest<HTMLElement>('[data-menu-item]')
		return li?.dataset.menuItem
	}

	private bindTabs() {
		this.tabs.forEach((tab) => {
			tab.addEventListener('click', () => {
				const cat = tab.dataset.menuTab
				if (!cat) return
				this.setCategory(cat)
			})
		})
	}

	private bindTiles() {
		this.tiles.forEach((tile) => {
			tile.addEventListener('click', () => {
				const handle = this.handleFromTile(tile)
				if (!handle) return
				this.activateItem(handle)
			})
		})
	}

	private bindDetail() {
		this.detailToggles.forEach((btn) => {
			btn.addEventListener('click', () => this.toggleDetail())
		})

		this.onKeydown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && this.dataset.detailShow === 'true') {
				this.setDetailOpen(false)
				this.activeToggle()?.focus()
			}
		}
		document.addEventListener('keydown', this.onKeydown)
	}

	private bindDismiss() {
		this.onDocClick = (e: MouseEvent) => {
			if (this.dataset.detailShow !== 'true') return
			const target = e.target as Node | null
			if (!target || !this.detailPanel) return
			if (!this.detailPanel.contains(target)) this.setDetailOpen(false)
		}
		this.onFloatingNavOpen = () => {
			if (this.dataset.detailShow === 'true') this.setDetailOpen(false)
		}
		document.addEventListener('click', this.onDocClick)
		document.addEventListener('floating-nav:open', this.onFloatingNavOpen)
	}

	private activeToggle(): HTMLButtonElement | undefined {
		// Toggle in the currently visible stack (the one with data-active='true').
		const activeStack = this.stacks.find((s) => s.dataset.active === 'true')
		if (!activeStack) return this.detailToggles[0]
		return q.os<HTMLButtonElement>('[data-menu-detail-toggle]', activeStack) ?? this.detailToggles[0]
	}

	private toggleDetail() {
		this.setDetailOpen(this.dataset.detailShow !== 'true')
	}

	private setDetailOpen(open: boolean) {
		const expanded = open ? 'true' : 'false'
		this.detailToggles.forEach((btn) => btn.setAttribute('aria-expanded', expanded))
		if (open) {
			this.dataset.detailShow = 'true'
		} else {
			delete this.dataset.detailShow
		}
	}

	private setCategory(category: string) {
		if (this.dataset.activeCategory === category) return
		this.dataset.activeCategory = category
		this.setDetailOpen(false)

		this.tabs.forEach((tab) => {
			tab.setAttribute('aria-selected', String(tab.dataset.menuTab === category))
		})

		let firstVisible: HTMLElement | undefined
		this.items.forEach((item) => {
			const match = item.dataset.menuItemCategory === category
			if (match) {
				item.removeAttribute('hidden')
				if (!firstVisible) firstVisible = item
			} else {
				item.setAttribute('hidden', '')
			}
		})

		const activeHandle = this.activeHandle()
		const activeStillVisible = activeHandle && this.items.find(
			(i) => i.dataset.menuItem === activeHandle && !i.hasAttribute('hidden')
		)
		if (!activeStillVisible && firstVisible) {
			const handle = firstVisible.dataset.menuItem
			if (handle) this.activateItem(handle)
		}
	}

	private activeHandle(): string | undefined {
		const pressed = this.tiles.find((t) => t.getAttribute('aria-pressed') === 'true')
		return pressed ? this.handleFromTile(pressed) : undefined
	}

	private activateItem(handle: string) {
		this.setDetailOpen(false)

		this.tiles.forEach((tile) => {
			const match = this.handleFromTile(tile) === handle
			tile.setAttribute('aria-pressed', String(match))
			tile.classList.toggle('is-active', match)
		})

		this.stageImages.forEach((img) => {
			this.markStageImage(img.dataset.menuStageImage ?? '', img.dataset.menuStageImage === handle)
		})

		this.stacks.forEach((stack) => {
			const match = stack.dataset.menuStack === handle
			if (match) {
				stack.dataset.active = 'true'
				stack.setAttribute('aria-hidden', 'false')
			} else {
				delete stack.dataset.active
				stack.setAttribute('aria-hidden', 'true')
			}
		})
	}

	private markStageImage(handle: string, active: boolean) {
		const wrap = this.stageImages.find((s) => s.dataset.menuStageImage === handle)
		if (!wrap) return
		if (active) {
			wrap.removeAttribute('hidden')
			wrap.removeAttribute('aria-hidden')
			wrap.dataset.active = 'true'
		} else {
			wrap.setAttribute('hidden', '')
			wrap.setAttribute('aria-hidden', 'true')
			delete wrap.dataset.active
		}
	}
}
